/**
 * Atlas manifests — declarative descriptions of every sprite the renderer
 * loads.
 *
 * # What lives here
 *
 * Pure data + small helpers. No PixiJS imports. No I/O. No side effects.
 * Two top-level manifest exports plus the helpers used to construct
 * them programmatically:
 *
 *   - `COMPACT_BIOME_MANIFEST` — every Compact-biome tile, prop, building,
 *     and door (~50 entries). Hand-listed to match the on-disk asset
 *     filenames exactly.
 *   - `PRIME_CHARACTER_MANIFEST` — every Prime sprite frame across 5
 *     animations × 8 directions (768 entries). Generated programmatically
 *     from the per-animation metadata table; the manifest itself is
 *     constructed at module load and cached.
 *   - `DIRECTIONS_8` — the canonical 8-direction string array. Order
 *     matches the engine's integer index 0-7 (E, NE, N, NW, W, SW, S, SE).
 *   - `directionStringToFacing` / `facingToDirectionString` — round-trip
 *     mapping helpers. The CombatEntity.facing field is an integer; the
 *     manifest path lookup needs the string. These two helpers keep the
 *     mapping in one place.
 *
 * # Per-pack loading strategy (LOCKED Layer 2)
 *
 * Each manifest entry declares its own loading shape via a discriminator.
 * The renderer doesn't pick a global strategy; each asset pack picks what
 * works for its specific characteristics:
 *
 *   - `kind: 'individual'` — one PNG per logical frame. Path resolves to
 *     a single image. Most flexible; works on any GPU; simplest renderer
 *     code; trades more HTTP requests for universal compatibility.
 *   - `kind: 'strip'` — one PNG holds N frames laid out horizontally
 *     (frame 0 at x=0, frame 1 at x=frameWidth, etc.). Renderer slices
 *     at runtime via texture frame rectangles. Fewer HTTP requests but
 *     constrained by GPU max texture size (commonly 4096-8192px).
 *
 * # Strategy chosen per pack (v1 launch)
 *
 *   - **Prime (AUTeddy Cyber Marksman)**: `individual`.
 *     Strips would be 13824×512 (Idle), exceeding the 4096-8192 GPU max
 *     on iOS Safari and older Android. Individual frames work on every
 *     device. 768 PNGs total; ~6 MB; lazy-loaded by the AtlasLoader.
 *
 *   - **Compact biome (maxparata Space Colony)**: `individual`.
 *     Pack ships individual files; no strips exist. Small N (~50).
 *
 *   - **Future enemy/Subroutine packs**: chosen per-pack at import time.
 *     The manifest type supports both shapes from day one; no refactor
 *     needed when packs that benefit from strips arrive.
 *
 * # Animation ID mapping
 *
 * Engine animation IDs are domain-clean (`'idle'`, `'shoot'`, `'death'`)
 * and do not echo the artist's filenames. The mapping lives in
 * `PRIME_ANIMATIONS`:
 *
 *   - `idle`     → source folder `Idle`         (frames `Idle_<dir>_NNNN.png`)
 *   - `walking`  → source folder `Walking`      (frames `Walking_<dir>_NNNN.png`)
 *   - `run`      → source folder `Run`          (frames `Run_<dir>_NNNN.png`)
 *   - `shoot`    → source folder `Magic_Attack` (frames `Magic_Attack_<dir>_NNNN.png`)
 *   - `death`    → source folder `Death_1`      (frames `Death_1_<dir>_NNNN.png`)
 *
 * The `_Rifle` variants from the AUTeddy pack are deliberately excluded —
 * Prime is an embodied AI in a combat shell, not a soldier holding a rifle.
 * The `Magic_Attack` animation is reused as the generic ranged-attack
 * "shoot" because it visually depicts an arm-cannon-style attack
 * appropriate for the Prime aesthetic.
 *
 * # Forward compatibility
 *
 *   - Adding a new biome = add a new manifest constant in this file.
 *   - Adding a new Architecture's sprites = same pattern as Prime; new
 *     animation/direction metadata + builder function.
 *   - Adding a strip-loading pack = use `kind: 'strip'` entries; the
 *     AtlasLoader already handles both shapes.
 *
 * @see {@link ./atlasLoader} — consumes manifests, produces loaded atlases
 * @see {@link ../../core/state/types.ts} — CombatEntity.facing integer convention
 */

// ─────────────────────────────────────────────────────────────────────────────
// 8-direction convention
//
// Order matches `CombatEntity.facing` integer index 0-7 documented in
// state/types.ts:
//
//   0 = E, 1 = SE, 2 = S, 3 = SW, 4 = W, 5 = NW, 6 = N, 7 = NE
//
// AUTeddy's pack uses these same direction string folder names, so no
// per-frame relabeling needed at copy time.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The 8-direction strings in engine-integer-index order.
 *
 * `DIRECTIONS_8[facing]` gives the direction string for a CombatEntity's
 * integer facing. The reverse lookup is `directionStringToFacing(str)`.
 */
export const DIRECTIONS_8 = [
  'E',
  'SE',
  'S',
  'SW',
  'W',
  'NW',
  'N',
  'NE',
] as const;

/**
 * Type alias for one of the 8 direction strings.
 */
export type Direction8 = (typeof DIRECTIONS_8)[number];

/**
 * Convert a direction string to the integer facing index used in
 * `CombatEntity.facing`. Returns -1 for unknown strings (callers must
 * check; -1 is not a valid facing).
 */
export function directionStringToFacing(s: Direction8): number {
  return DIRECTIONS_8.indexOf(s);
}

/**
 * Convert an integer facing (0-7) to its direction string. Throws on
 * out-of-range values rather than returning undefined — facing values
 * are produced by the combat reducer which is the source of truth, so
 * an out-of-range value is a programming error.
 *
 * @throws RangeError if facing is not in [0, 7]
 */
export function facingToDirectionString(facing: number): Direction8 {
  if (!Number.isInteger(facing) || facing < 0 || facing > 7) {
    throw new RangeError(
      `facingToDirectionString: facing must be integer in [0, 7], got ${facing}`,
    );
  }
  return DIRECTIONS_8[facing]!;
}

// ─────────────────────────────────────────────────────────────────────────────
// AtlasFrame — discriminated union of loading shapes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One sprite frame, stored as its own PNG.
 *
 * The renderer treats this as a complete `Texture` after load — no slicing,
 * no frame-rect math, no edge cases.
 */
export interface IndividualFrameEntry {
  readonly kind: 'individual';

  /**
   * Stable engine-side identifier. Format: dot-separated namespace, e.g.
   * `'compact.floor.01'`, `'prime.idle.E.1'`. The renderer looks up
   * textures by this ID; consumers should never need to know the URL.
   */
  readonly id: string;

  /** Public URL path under `/assets/...`. Vite resolves this at build time. */
  readonly path: string;
}

/**
 * One sprite-strip PNG containing N horizontal frames.
 *
 * The renderer loads the strip texture once and produces N frame textures
 * by slicing via PixiJS `Texture` constructor with explicit frame rect.
 *
 * Used when the asset pack already provides strips AND the strip width
 * (`frameCount × frameWidth`) fits under the target GPU max texture size
 * (4096 for broad mobile compat, 8192 for desktop-only).
 */
export interface StripFrameEntry {
  readonly kind: 'strip';

  /**
   * Stable engine-side identifier of the STRIP itself (e.g.
   * `'enemy.compact_drone.walking.E'`). Individual frame IDs derived from
   * this are produced by the AtlasLoader as `${stripId}.${frameIndex}`.
   */
  readonly id: string;

  /** Public URL path. */
  readonly path: string;

  /** Number of frames in the strip (laid out horizontally, left to right). */
  readonly frameCount: number;

  /** Pixel width of each frame in the strip. */
  readonly frameWidth: number;

  /** Pixel height of each frame (== strip height). */
  readonly frameHeight: number;
}

/**
 * Union of all atlas frame loading shapes. Add new variants here when new
 * loading patterns appear (e.g. `kind: 'spritesheet_json'` if we add
 * TexturePacker output support later).
 */
export type AtlasFrame = IndividualFrameEntry | StripFrameEntry;

/**
 * A logical atlas — a collection of frame entries that the AtlasLoader
 * loads as a unit. Manifests are constructed at module load time; the
 * loader treats them as immutable inputs.
 */
export interface AtlasManifest {
  /** Human-readable name for this manifest, surfaces in error logs. */
  readonly name: string;

  /** All frame entries this manifest contains. */
  readonly frames: readonly AtlasFrame[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Compact biome manifest
//
// Files live under aevons-wake-play/public/assets/{tiles,props,buildings,
// doors}/compact/. Vite serves the public directory at the URL root, so
// runtime URLs are /assets/<category>/compact/<filename>.png.
//
// Counts (verified May 6, 2026):
//   - 12 floors      (floor_01.png ... floor_12.png)
//   - 20 floor patches (floor_patch_01.png ... floor_patch_20.png)
//   - 19 props        (6 craters + 13 rocks)
//   - 7 buildings     (building_01.png ... building_07.png)
//   - 2 doors         (door_01.png, door_02.png)
//   Total: 60 entries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Helper: build a sequence of individual-frame entries with a shared
 * id/path prefix and zero-padded numeric suffix.
 *
 * Example: `buildSequence('compact.floor', '/assets/tiles/compact/floor', 12, 2)`
 * produces 12 entries with IDs `compact.floor.01` through `compact.floor.12`.
 */
function buildSequence(
  idPrefix: string,
  pathPrefix: string,
  count: number,
  padWidth: number,
  pathSuffix = '.png',
): IndividualFrameEntry[] {
  const out: IndividualFrameEntry[] = [];
  for (let i = 1; i <= count; i++) {
    const padded = String(i).padStart(padWidth, '0');
    out.push({
      kind: 'individual',
      id: `${idPrefix}.${padded}`,
      path: `${pathPrefix}_${padded}${pathSuffix}`,
    });
  }
  return out;
}

/**
 * Compact biome — every tile, prop, building, and door PNG that ships
 * with the maxparata Isometric Space Colony pack.
 *
 * Source pack: maxparata "Isometric Space Colony" (full version), $10.
 * License: itch.io commercial-OK with modification rights.
 * License registry: docs/AevonsWake_ASSETS.md
 */
export const COMPACT_BIOME_MANIFEST: AtlasManifest = {
  name: 'compact-biome',
  frames: [
    // 12 floor tiles
    ...buildSequence('compact.floor', '/assets/tiles/compact/floor', 12, 2),
    // 20 floor patch overlays
    ...buildSequence(
      'compact.floor_patch',
      '/assets/tiles/compact/floor_patch',
      20,
      2,
    ),
    // 6 craters
    ...buildSequence(
      'compact.prop.crater',
      '/assets/props/compact/prop_crater',
      6,
      2,
    ),
    // 13 rocks
    ...buildSequence(
      'compact.prop.rock',
      '/assets/props/compact/prop_rock',
      13,
      2,
    ),
    // 7 buildings
    ...buildSequence(
      'compact.building',
      '/assets/buildings/compact/building',
      7,
      2,
    ),
    // 2 doors
    ...buildSequence('compact.door', '/assets/doors/compact/door', 2, 2),
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Prime character manifest — programmatically generated
//
// 5 animations × 8 directions × N frames = 768 entries.
//
// The metadata table below is the single source of truth for frame counts
// and source-folder mappings. The builder walks the table to produce the
// AtlasManifest. Verified frame counts via PowerShell recon May 6, 2026.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Engine animation ID — the stable in-game identifier for each Prime
 * animation state. These IDs flow through the entire codebase
 * (combat reducer state machine, sprite renderer current-frame lookup,
 * animation timing tables) so they MUST stay stable across versions.
 */
export type PrimeAnimationId =
  | 'idle'
  | 'walking'
  | 'run'
  | 'shoot'
  | 'death';

/**
 * Per-animation metadata: how many frames does the source pack ship,
 * and which source folder + filename prefix do the frames live under.
 *
 * Frame counts are the authoritative truth for animation playback; the
 * spriteRenderer in Layer 4 will read these directly when computing
 * `currentFrame = (tick - animStartTick) % frameCount`.
 *
 * `sourcePrefix` is the artist's folder/filename prefix in the AUTeddy
 * pack. The import script copies frames into renderer-friendly paths
 * (`/assets/characters/prime/<engineId>/<dir>/Frame_NNNN.png`) but
 * preserves the original source-prefix in the per-frame filename for
 * debuggability.
 */
export interface PrimeAnimationMeta {
  readonly engineId: PrimeAnimationId;
  readonly frameCount: number;
  /**
   * Number of simulation ticks per animation frame. Animation
   * advances one frame every `framesPerStep` ticks. At 30Hz sim:
   *   - framesPerStep: 4 → ~133ms per frame (calm idle)
   *   - framesPerStep: 3 → ~100ms per frame (walking pace)
   *   - framesPerStep: 2 → ~67ms per frame (run, attacks)
   *
   * Total animation duration: `frameCount * framesPerStep` ticks.
   * For idle (27 × 4 = 108 ticks ≈ 3.6s loop).
   */
  readonly framesPerStep: number;
  /**
   * The artist's source-folder name in the AUTeddy pack
   * (e.g. `'Magic_Attack'`). Frame filenames in the pack are
   * `${sourcePrefix}_${dir}_${nnnn}.png`. The import script copies
   * frames into renderer-friendly paths but preserves this prefix
   * in the per-frame filename for debuggability.
   */
  readonly sourcePrefix: string;
}

/**
 * The full Prime animation table. Adding a new animation means adding a
 * row here AND running the import script. Frame counts MUST match the
 * counts on disk or the AtlasLoader's missing-asset fallback fires for
 * each missing frame (visible as magenta sprites in dev).
 *
 * Verified via PowerShell recon May 6, 2026:
 *   Idle         : 27 frames per direction (8 dirs)  → 216 frames total
 *   Walking      : 17 frames per direction          → 136 frames
 *   Run          : 13 frames per direction          → 104 frames
 *   Magic_Attack : 21 frames per direction          → 168 frames
 *   Death_1      : 18 frames per direction          → 144 frames
 *   ────────────────────────────────────────────────────────────
 *   Total        :                                    768 frames
 */
export const PRIME_ANIMATIONS: readonly PrimeAnimationMeta[] = [
  { engineId: 'idle',    frameCount: 27, framesPerStep: 4, sourcePrefix: 'Idle' },
  { engineId: 'walking', frameCount: 17, framesPerStep: 3, sourcePrefix: 'Walking' },
  { engineId: 'run',     frameCount: 13, framesPerStep: 2, sourcePrefix: 'Run' },
  { engineId: 'shoot',   frameCount: 21, framesPerStep: 2, sourcePrefix: 'Magic_Attack' },
  { engineId: 'death',   frameCount: 18, framesPerStep: 3, sourcePrefix: 'Death_1' },
];

/**
 * Lookup table from engine ID to metadata. Populated lazily on first
 * access; pure function of the table above.
 */
const PRIME_ANIMATIONS_BY_ID: ReadonlyMap<PrimeAnimationId, PrimeAnimationMeta> =
  new Map(PRIME_ANIMATIONS.map((m) => [m.engineId, m]));

/**
 * Get animation metadata by engine ID. Throws on unknown ID rather than
 * returning undefined — the table is the source of truth, missing IDs
 * are programming errors.
 *
 * @throws Error if `id` is not a known animation
 */
export function getPrimeAnimationMeta(id: PrimeAnimationId): PrimeAnimationMeta {
  const meta = PRIME_ANIMATIONS_BY_ID.get(id);
  if (!meta) {
    throw new Error(`getPrimeAnimationMeta: unknown animation id "${id}"`);
  }
  return meta;
}

/**
 * Generate the full Prime character manifest by walking the animation
 * table × directions × frames.
 *
 * Frame ID format: `prime.<engineId>.<dir>.<frameIndex>` where
 * frameIndex is 1-based to match the artist's filenames
 * (`Idle_NE_0001.png` → frameIndex 1).
 *
 * Path format: `/assets/characters/prime/<engineId>/<dir>/<sourcePrefix>_<dir>_<NNNN>.png`
 * where NNNN is the 4-digit zero-padded frame index. The path uses the
 * engine ID for the directory but preserves the artist's source prefix
 * in the filename — this lets us inspect the file in DevTools and see
 * exactly which source PNG it corresponds to.
 *
 * Why we generate this at module-load instead of writing 768 entries
 * by hand: maintainability. If a future patch changes Magic_Attack's
 * frame count from 21 to 22, we change one number in PRIME_ANIMATIONS
 * and 8 manifest entries appear automatically.
 */
export function buildPrimeCharacterManifest(): AtlasManifest {
  const frames: IndividualFrameEntry[] = [];

  for (const anim of PRIME_ANIMATIONS) {
    for (const dir of DIRECTIONS_8) {
      for (let i = 1; i <= anim.frameCount; i++) {
        const padded = String(i).padStart(4, '0');
        frames.push({
          kind: 'individual',
          id: `prime.${anim.engineId}.${dir}.${i}`,
          path: `/assets/characters/prime/${anim.engineId}/${dir}/${anim.sourcePrefix}_${dir}_${padded}.png`,
        });
      }
    }
  }

  return {
    name: 'prime-character',
    frames,
  };
}

/**
 * Pre-built Prime character manifest. Module-load-time computation; safe
 * to import and use without paying the construction cost twice.
 */
export const PRIME_CHARACTER_MANIFEST: AtlasManifest = buildPrimeCharacterManifest();

/**
 * Total frame count expected in the Prime manifest, derived from the
 * animation table. Useful for tests + load-progress UI ("loaded 312/768
 * frames").
 */
export const PRIME_FRAME_COUNT_TOTAL: number = PRIME_ANIMATIONS.reduce(
  (sum, m) => sum + m.frameCount * DIRECTIONS_8.length,
  0,
);
